'use client';
// 導覽列 - 搜尋筐

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import bannerClient from '@/common/apiClient/bannerClient';
import Image from 'next/image';

const NavSearch = ({ locale }) => {
    const router = useRouter();
    const { t } = useTranslation();
    const timerRef = useRef(null);

    const [searchKeyword, setSearchKeyword] = useState('');
    const [hotWord, setHotWord] = useState([]);
    const [initOption, setInitOption] = useState([]);
    const [index, setIndex] = useState(0);
    const [showSearch, setShowSearch] = useState(false);

    const currentHotWord = useMemo(() => hotWord[index] || '', [hotWord, index]);

    const onSearch = useCallback(() => {
        const word = searchKeyword.trim() || currentHotWord;
        if (word) {
            router.push(`/search/${encodeURIComponent(word)}`);
            setSearchKeyword(word);
            setShowSearch(false);
        }
    }, [searchKeyword, currentHotWord, router]);

    const onKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter' && searchKeyword.trim()) {
                onSearch();
            }
        },
        [searchKeyword, onSearch]
    );

    const onFocus = () => {
        if (!searchKeyword.trim()) {
            clearInterval(timerRef.current);
            setHotWord([]);
        }
    };

    const onBlur = () => {
        if (!searchKeyword.trim()) {
            setHotWord(initOption);
            startHotKeywordRotation();
        }
    };

    const startHotKeywordRotation = useCallback(() => {
        clearInterval(timerRef.current);
        if (hotWord?.length > 1) {
            timerRef.current = setInterval(() => {
                setIndex((prev) => (prev + 1) % hotWord?.length);
            }, 2000);
        }
    }, [hotWord]);

    const getSearchOption = useCallback(async () => {
        try {
            const { searchOption } = await bannerClient.getSearchOptions(locale);
            if (searchOption?.length) {
                setInitOption(searchOption);
                setHotWord(searchOption);
            }
        } catch (error) {
            console.error(error);
        }
    }, [locale]);

    useEffect(() => {
        getSearchOption();
    }, [getSearchOption]);

    useEffect(() => {
        if (hotWord.length > 0) startHotKeywordRotation();
    }, [hotWord, startHotKeywordRotation]);

    useEffect(() => {
        return () => clearInterval(timerRef.current);
    }, []);

    return (
        <>
            <div className="hidden ulg:between-center flex-grow">
                {/* 手機版 logo */}
                <a href="/" className="flex-shrink-0">
                    <Image
                        className="uxs:w-[104px] uxs:h-[28px]"
                        width={138}
                        height={38}
                        src="/images/layout/idle_logo.svg"
                        alt="logo"
                    />
                </a>

                {/* 手機版搜尋按鈕 */}
                <button
                    onClick={() => setShowSearch(true)}
                    className="gtm-icon-search w-[32px] group aspect-[1] trans 
                    border-1 rounded-[50%] center-center border-white border-opacity-[.32] hover:bg-[rgba(255,255,255,.3)] hover:border-opacity-[1]"
                >
                    <Image
                        className="filter-white"
                        width={22}
                        height={22}
                        src="/images/icon/icon_search.svg"
                        alt="icon"
                    />
                </button>
            </div>

            <div
                className={`flex-grow px-[12px] between-center max-w-[350px] rounded-[40px] flex gap-x-[12px] py-[4px] border-1 trans
                 border-white border-opacity-[.3] ulg:w-screen ulg:h-[53px] ulg:trans ulg:duration-400 ulg:absolute ulg:top-0 ulg:z-[10]
                 ulg:max-w-[unset] ${
                     showSearch ? 'ulg:left-0' : 'ulg:left-[100%]'
                 } ulg:px-[15px] ulg:flex ulg:items-center ulg:gap-x-[15px] ulg:border-0 ulg:rounded-none ulg:bg-[rgba(0,0,0)]`}
            >
                {/* 手機版顯示 */}
                <button
                    onClick={() => setShowSearch(false)}
                    className="hidden ulg:img-box w-[14px] aspect-[1] flex-shrink-0"
                >
                    <Image
                        width={14}
                        height={14}
                        src="/images/icon/icon_search_arrow.svg"
                        alt="icon"
                    />
                </button>

                {/* 輸入框 */}
                <input
                    className="flex-grow text-btn1 bg-transparent line-clamp-1 max-w-[calc(100%-34px)] text-white placeholder:text-white
                    ulg:flex-grow ulg:rounded-[40px] ulg:h-[32px] ulg:px-[16px] ulg:py-0 ulg:bg-black umd:text-bd1m"
                    value={searchKeyword}
                    placeholder={hotWord[index]}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    enterKeyHint="search"
                />

                {/* 手機搜尋按鈕 */}
                <button
                    onClick={onSearch}
                    className="gtm-btn-search hidden ulg:inline-block text-btn4 text-bou trans hover:text-purple9 flex-shrink-0"
                >
                    {t('SEARCH')}
                </button>

                {/* 放大鏡 */}
                <button
                    onClick={onSearch}
                    className="gtm-btn-search trans hover:opacity-[.6] ulg:hidden"
                >
                    <Image
                        width={22}
                        height={22}
                        src="/images/icon/icon_search.svg"
                        alt="icon"
                        className="pointer-events-none"
                    />
                </button>
            </div>
        </>
    );
};

export default NavSearch;
